import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/api';
import { useSearchParams } from 'react-router-dom';
import TablePreferencesPanel from '../../components/TablePreferencesPanel';
import { TablePreferenceColumn, useTablePreferences } from '../../hooks/useTablePreferences';
import { useAppSelector } from '../../store/hooks';
import { InventoryBalanceItem, InventoryLotQualitySummary, InventoryLotTrace } from '../../types';
import { summarizeUnpostedWriteOffPeriods } from '../../utils/inventoryUnpostedWriteOffPeriods';
import { inventoryMovementPeriods, selectInventoryMovements } from '../../utils/inventoryMovementPeriods';
import InventoryAttentionPanel from '../../components/InventoryAttentionPanel';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';
import './InventoryBalancePage.css';

const thStyle: React.CSSProperties = { padding: '10px', textAlign: 'left', background: 'var(--crm-navbar-dropdown-bg)', color: 'var(--crm-primary-contrast)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--crm-table-row-border)' };

type SortKey = 'categoryName' | 'productName' | 'weightType' | 'stockIn' | 'stockOut' | 'balance';
type SortDirection = 'asc' | 'desc';
type InventoryBalanceColumnKey = 'category' | 'product' | 'unit' | 'stockIn' | 'lots' | 'stockOut' | 'balance';

const INVENTORY_BALANCE_COLUMNS: readonly TablePreferenceColumn<InventoryBalanceColumnKey>[] = [
  { key: 'category', label: 'Категорія', defaultWidth: 13, minWidth: 8, maxWidth: 25 },
  { key: 'product', label: 'Продукт', defaultWidth: 25, minWidth: 15, maxWidth: 40, locked: true },
  { key: 'unit', label: 'Одиниця', defaultWidth: 8, minWidth: 6, maxWidth: 15 },
  { key: 'stockIn', label: 'Прихід', defaultWidth: 12, minWidth: 8, maxWidth: 22 },
  { key: 'lots', label: 'Партії / ризики', defaultWidth: 17, minWidth: 12, maxWidth: 28 },
  { key: 'stockOut', label: 'Витрата', defaultWidth: 12, minWidth: 8, maxWidth: 22 },
  { key: 'balance', label: 'Залишок', defaultWidth: 13, minWidth: 10, maxWidth: 25, locked: true },
];
const INVENTORY_BALANCE_PREFERENCES_KEY = 'crm4chef.inventory-balance.table-preferences.v1';
const categoryOf = (item: InventoryBalanceItem) => item.categoryName || 'Без категорії';
const compareBalanceItems = (a: InventoryBalanceItem, b: InventoryBalanceItem, key: SortKey, direction: SortDirection) => {
  const aValue = key === 'categoryName' ? categoryOf(a) : a[key];
  const bValue = key === 'categoryName' ? categoryOf(b) : b[key];
  const comparison = typeof aValue === 'number' && typeof bValue === 'number'
    ? aValue - bValue
    : String(aValue || '').localeCompare(String(bValue || ''), 'uk');
  return (direction === 'asc' ? 1 : -1) * (comparison || a.productName.localeCompare(b.productName, 'uk'));
};

interface InventoryProductMovement {
  id: string;
  movementDate: string;
  documentDate: string;
  type: string;
  label: string;
  status: string;
  quantityDelta: number;
  pendingDelta: number | null;
  balanceAfter: number;
  isStockoutTransition: boolean;
  isRevisionBaseline: boolean;
  createdAt: string;
  postedAtUtc: string | null;
  postedByUsername: string | null;
  notes: string;
}

const getMovementNote = (note: string) => {
  const trimmed = note.trim();
  if (!trimmed.startsWith('{')) return note;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object'
      && 'v' in parsed && parsed.v === 2
      && 'rows' in parsed && Array.isArray(parsed.rows)
      && 'note' in parsed && typeof parsed.note === 'string') {
      return parsed.note.trim();
    }
  } catch {
    return note;
  }

  return note;
};

const InventoryBalancePage: React.FC = () => {
  const [contextParams] = useSearchParams();
  const username = useAppSelector((state) => state.auth.username);
  const canUpdateQuality = useAppSelector(state => state.auth.permissions.includes('page.management.inventory'));
  const {
    preferences: tablePreferences,
    setWidth,
    setVisible,
    setDensity,
    reset: resetTablePreferences,
  } = useTablePreferences(INVENTORY_BALANCE_PREFERENCES_KEY, username, INVENTORY_BALANCE_COLUMNS);
  const visibleColumns = INVENTORY_BALANCE_COLUMNS.filter(column => tablePreferences.visibility[column.key]);
  const visibleWidthTotal = visibleColumns.reduce((total, column) => total + tablePreferences.widths[column.key], 0);
  const widthScale = visibleWidthTotal > 0 ? 100 / visibleWidthTotal : 1;
  const [items, setItems] = useState<InventoryBalanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('categoryName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
  const [lotsByProduct, setLotsByProduct] = useState<Record<number, InventoryLotTrace[]>>({});
  const [lotsLoadingId, setLotsLoadingId] = useState<number | null>(null);
  const [lotErrors, setLotErrors] = useState<Record<number, string>>({});
  const [movementProductId, setMovementProductId] = useState<number | null>(null);
  const [movementsByProduct, setMovementsByProduct] = useState<Record<number, InventoryProductMovement[]>>({});
  const [movementsLoadingId, setMovementsLoadingId] = useState<number | null>(null);
  const [movementErrors, setMovementErrors] = useState<Record<number, string>>({});
  const [movementPeriod, setMovementPeriod] = useState('current');
  const [includeEarlierMovements, setIncludeEarlierMovements] = useState(false);
  const [qualitySummary, setQualitySummary] = useState<InventoryLotQualitySummary | null>(null);
  const [qualityUpdatingId, setQualityUpdatingId] = useState<number | null>(null);
  const [warehouseScope, setWarehouseScope] = useState(() => contextParams.get('warehouseId') || '');
  const warehouseParams = useCallback(() => warehouseScope === 'unassigned'
    ? { unassignedOnly: true }
    : warehouseScope ? { warehouseId: Number(warehouseScope) } : {}, [warehouseScope]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const scopedParams = { currentOnly: true, ...warehouseParams() };
      const [balanceResponse, qualityResponse] = await Promise.all([
        api.get<InventoryBalanceItem[]>('/inventorybalance', { params: scopedParams }),
        api.get<InventoryLotQualitySummary>('/inventorybalance/quality-alerts', { params: scopedParams }),
      ]);
      setItems(balanceResponse.data);
      setQualitySummary(qualityResponse.data);
      setLotsByProduct({});
      setMovementsByProduct({});
      setExpandedProductId(null);
      setMovementProductId(null);
    } catch {
      setItems([]);
      setQualitySummary(null);
      setError('Не вдалося завантажити залишки товарів.');
    } finally {
      setLoading(false);
    }
  }, [warehouseParams]);

  const toggleMovements = async (productId: number) => {
    if (movementProductId === productId) {
      setMovementProductId(null);
      return;
    }
    setMovementProductId(productId);
    setMovementPeriod('current');
    setIncludeEarlierMovements(false);
    if (movementsByProduct[productId]) return;
    setMovementErrors(current => ({ ...current, [productId]: '' }));
    setMovementsLoadingId(productId);
    try {
      const response = await api.get<InventoryProductMovement[]>(`/inventorybalance/${productId}/movements`, { params: warehouseParams() });
      setMovementsByProduct(current => ({ ...current, [productId]: response.data }));
    } catch {
      setMovementErrors(current => ({ ...current, [productId]: 'Не вдалося завантажити журнал руху. Перевірте підключення до сервера та оновіть сторінку.' }));
    } finally {
      setMovementsLoadingId(current => current === productId ? null : current);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const allChronologicalMovements = movementProductId === null
    ? []
    : [...(movementsByProduct[movementProductId] || [])].reverse();
  const movementPeriods = inventoryMovementPeriods(allChronologicalMovements);
  const chronologicalMovements = selectInventoryMovements(allChronologicalMovements, movementPeriod, includeEarlierMovements);
  const unpostedWriteOffPeriods = summarizeUnpostedWriteOffPeriods(allChronologicalMovements).filter(period => {
    if (movementPeriod === 'all') return true;
    const selected = movementPeriod === 'current' ? movementPeriods[movementPeriods.length - 1] : movementPeriods.find(item => item.id === movementPeriod);
    if (!selected) return true;
    const startIndex = period.startRevision ? allChronologicalMovements.findIndex(item => item === period.startRevision) : -1;
    return startIndex < selected.end && (includeEarlierMovements || startIndex >= selected.start || (selected.id === 'before-first' && startIndex === -1));
  });

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.categoryName || 'Без категорії'))).sort(),
    [items]
  );

  const filtered = items
    .filter((i) => !categoryFilter || (i.categoryName || 'Без категорії') === categoryFilter)
    .filter((i) => !search || i.productName.toLowerCase().includes(search.toLowerCase()));

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => compareBalanceItems(a, b, sortKey, sortDirection));
  }, [filtered, sortDirection, sortKey]);

  const categoryCounts = sorted.reduce<Record<string, number>>((counts, item) => {
    const category = categoryOf(item);
    counts[category] = (counts[category] || 0) + 1;
    return counts;
  }, {});
  const displayed = sortKey === 'categoryName'
    ? sorted.filter(item => !collapsedCategories.has(categoryOf(item)))
    : sorted;
  const pageCount = Math.max(1, Math.ceil(displayed.length / pageSize));
  const currentPage = Math.min(pageIndex, pageCount - 1);
  const pageItems = displayed.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const toggleCategory = (category: string) => {
    setCollapsedCategories(current => {
      const next = new Set(current);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
    setPageIndex(0);
  };

  const toggleSort = (key: SortKey) => {
    setPageIndex(0);
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '');

  const toggleLots = async (productId: number) => {
    if (expandedProductId === productId) {
      setExpandedProductId(null);
      return;
    }
    setExpandedProductId(productId);
    if (lotsByProduct[productId]) return;
    setLotErrors(current => ({ ...current, [productId]: '' }));
    setLotsLoadingId(productId);
    try {
      const response = await api.get<InventoryLotTrace[]>(`/inventorybalance/${productId}/lots`, { params: warehouseParams() });
      setLotsByProduct(current => ({ ...current, [productId]: response.data }));
    } catch {
      setLotErrors(current => ({ ...current, [productId]: 'Не вдалося завантажити простежуваність партій. Закрийте та відкрийте партії для повторної спроби.' }));
    } finally {
      setLotsLoadingId(current => current === productId ? null : current);
    }
  };

  const updateLotQuality = async (
    productId: number,
    receiptItemId: number,
    status: 'Available' | 'Hold' | 'Recalled'
  ) => {
    let reason = '';
    if (status !== 'Available') {
      reason = window.prompt(status === 'Hold' ? 'Причина карантину партії:' : 'Причина відкликання партії:')?.trim() || '';
      if (!reason) return;
    }
    setQualityUpdatingId(receiptItemId);
    try {
      await api.put(`/inventorybalance/lots/${receiptItemId}/quality`, { status, reason });
      const [lotsResponse, qualityResponse, balanceResponse] = await Promise.all([
        api.get<InventoryLotTrace[]>(`/inventorybalance/${productId}/lots`, { params: warehouseParams() }),
        api.get<InventoryLotQualitySummary>('/inventorybalance/quality-alerts', { params: { currentOnly: true, ...warehouseParams() } }),
        api.get<InventoryBalanceItem[]>('/inventorybalance', { params: { currentOnly: true, ...warehouseParams() } }),
      ]);
      setLotsByProduct(current => ({ ...current, [productId]: lotsResponse.data }));
      setQualitySummary(qualityResponse.data);
      setItems(balanceResponse.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося змінити статус партії.');
    } finally {
      setQualityUpdatingId(null);
    }
  };

  const qualityLabel = (status: InventoryLotTrace['qualityStatus']) =>
    status === 'Hold' ? 'Карантин' : status === 'Recalled' ? 'Відкликано' : 'Доступна';

  const openAttention = (productId: number, target: 'lots' | 'movements') => {
    setCategoryFilter('');
    setSearch('');
    setCollapsedCategories(new Set());
    // Use the current sort without filters so off-page attention links reveal their target.
    const allSorted = [...items].sort((a, b) => compareBalanceItems(a, b, sortKey, sortDirection));
    const targetPage = Math.floor(allSorted.findIndex(item => item.productId === productId) / pageSize);
    setPageIndex(Math.max(0, targetPage));
    if (target === 'lots' && expandedProductId !== productId) void toggleLots(productId);
    if (target === 'movements' && movementProductId !== productId) void toggleMovements(productId);
    window.setTimeout(() => {
      const detail = document.getElementById(`inventory-${target}-${productId}`);
      detail?.scrollIntoView?.({ block: 'nearest' });
      detail?.focus();
    }, 0);
  };

  return (
    <div className="inventory-balance-page" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Залишки товарів на складі</h2>

      <div className="inventory-balance-filters" style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', margin: '16px 0', flexWrap: 'wrap' }}>
        <InventoryWarehouseSelect
          label="Облік"
          value={warehouseScope}
          onChange={(value) => { setWarehouseScope(value); setPageIndex(0); }}
          required={false}
          allLabel="Усі склади (загальний)"
          includeUnassigned
        />
        <label>
          Категорія:
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPageIndex(0); }} style={{ display: 'block', padding: '6px', marginTop: '4px', minWidth: '200px' }}>
            <option value="">Всі категорії</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label>
          Пошук:
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPageIndex(0); }} placeholder="Назва товару..." style={{ display: 'block', padding: '6px', marginTop: '4px' }} />
        </label>
        <button
          onClick={load}
          disabled={loading}
          style={{ padding: '10px 20px', background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          {loading ? 'Оновлення...' : '🔄 Оновити'}
        </button>
      </div>

      <InventoryAttentionPanel
        items={items}
        alerts={qualitySummary?.alerts || []}
        loading={loading}
        error={error}
        onLots={productId => openAttention(productId, 'lots')}
        onMovements={productId => openAttention(productId, 'movements')}
      />

      <TablePreferencesPanel
        columns={INVENTORY_BALANCE_COLUMNS}
        preferences={tablePreferences}
        onWidthChange={setWidth}
        onVisibilityChange={setVisible}
        onDensityChange={setDensity}
        onReset={resetTablePreferences}
      />
      {!loading && !error && sorted.length > 0 && (
        <div className="inventory-balance-navigation">
          <span>Знайдено: {sorted.length} товарів · на сторінці: {pageItems.length}</span>
          <label>Рядків на сторінці
            <select value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPageIndex(0); }}>
              <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </select>
          </label>
          {sortKey === 'categoryName' && <>
            <button type="button" onClick={() => { setCollapsedCategories(new Set(Object.keys(categoryCounts))); setPageIndex(0); }}>Згорнути всі категорії</button>
            <button type="button" onClick={() => { setCollapsedCategories(new Set()); setPageIndex(0); }}>Розгорнути всі категорії</button>
          </>}
        </div>
      )}
      {sortKey === 'categoryName' && collapsedCategories.size > 0 && (
        <div className="inventory-balance-navigation" aria-label="Згорнуті категорії">
          {Object.keys(categoryCounts).filter(category => collapsedCategories.has(category)).map(category => (
            <button key={category} type="button" aria-expanded={false} onClick={() => toggleCategory(category)}>
              Розгорнути: {category} ({categoryCounts[category]})
            </button>
          ))}
        </div>
      )}
      <table className={`crm-table crm-mobile-table-scroll crm-mobile-table-scroll--wide ${tablePreferences.density === 'compact' ? 'crm-table--compact' : ''}`} style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
        <colgroup>
          {visibleColumns.map(column => (
            <col key={column.key} style={{ width: `${(tablePreferences.widths[column.key] * widthScale).toFixed(2)}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {tablePreferences.visibility.category && <th style={thStyle}>
              <button type="button" onClick={() => toggleSort('categoryName')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'inherit' }}>
                Категорія{sortIndicator('categoryName')}
              </button>
            </th>}
            {tablePreferences.visibility.product && <th style={thStyle}>
              <button type="button" onClick={() => toggleSort('productName')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'inherit' }}>
                Продукт{sortIndicator('productName')}
              </button>
            </th>}
            {tablePreferences.visibility.unit && <th style={thStyle}>
              <button type="button" onClick={() => toggleSort('weightType')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'inherit' }}>
                Одиниця{sortIndicator('weightType')}
              </button>
            </th>}
            {tablePreferences.visibility.stockIn && <th style={{ ...thStyle, textAlign: 'right' }}>
              <button type="button" onClick={() => toggleSort('stockIn')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'inherit' }}>
                Прихід{sortIndicator('stockIn')}
              </button>
            </th>}
            {tablePreferences.visibility.lots && <th style={{ ...thStyle, textAlign: 'right' }}>Партії / ризики</th>}
            {tablePreferences.visibility.stockOut && <th style={{ ...thStyle, textAlign: 'right' }}>
              <button type="button" onClick={() => toggleSort('stockOut')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'inherit' }}>
                Витрата{sortIndicator('stockOut')}
              </button>
            </th>}
            {tablePreferences.visibility.balance && <th style={{ ...thStyle, textAlign: 'right' }}>
              <button type="button" onClick={() => toggleSort('balance')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'inherit' }}>
                Залишок{sortIndicator('balance')}
              </button>
            </th>}
          </tr>
        </thead>
        <tbody>
          {pageItems.map((i, index) => (
            <React.Fragment key={i.productId}>
              {sortKey === 'categoryName' && (index === 0 || categoryOf(pageItems[index - 1]) !== categoryOf(i)) && (
                <tr>
                  <td colSpan={visibleColumns.length} className="inventory-balance-category">
                    <button type="button" aria-expanded={true} onClick={() => toggleCategory(categoryOf(i))}>
                      Згорнути: {categoryOf(i)} ({categoryCounts[categoryOf(i)]})
                    </button>
                  </td>
                </tr>
              )}
              <tr>
                {tablePreferences.visibility.category && <td style={tdStyle}>{i.categoryName || 'Без категорії'}</td>}
                {tablePreferences.visibility.product && <td style={tdStyle}>
                  <div style={{ display: 'grid', gap: 5, justifyItems: 'start' }}>
                    <strong>{i.productName}</strong>
                    <button type="button" onClick={() => toggleMovements(i.productId)} style={{ border: '1px solid var(--crm-info)', borderRadius: 5, padding: '4px 7px', cursor: 'pointer', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', fontSize: 11 }}>
                      {movementProductId === i.productId ? 'Сховати рухи' : 'Історія руху'}
                    </button>
                  </div>
                </td>}
                {tablePreferences.visibility.unit && <td style={tdStyle}>{i.weightType}</td>}
                {tablePreferences.visibility.stockIn && <td style={{ ...tdStyle, textAlign: 'right' }}>{i.stockIn.toFixed(2)}</td>}
                {tablePreferences.visibility.lots && <td style={{ ...tdStyle, textAlign: 'right' }}>
                  <button type="button" onClick={() => toggleLots(i.productId)} style={{ border: 0, borderRadius: 6, padding: '5px 8px', cursor: 'pointer', background: i.blockedQuantity > 0 ? 'var(--crm-blocked-bg)' : i.expiringSoonQuantity > 0 ? 'var(--crm-warning-bg)' : 'var(--crm-success-bg)', color: 'var(--crm-text)' }}>
                    {i.openLotCount} парт. {i.expiringSoonQuantity > 0 ? `· ${i.expiringSoonQuantity.toFixed(2)} ${i.weightType} до 14 днів / прострочено` : ''}
                  </button>
                  {i.blockedQuantity > 0 && <div style={{ color: 'var(--crm-blocked)', fontSize: 10, marginTop: 3 }}>заблоковано: {i.blockedQuantity.toFixed(2)}</div>}
                  {i.untracedWriteOffQuantity > 0 && <div style={{ color: 'var(--crm-warning)', fontSize: 10, marginTop: 3 }}>без прив’язки до партій: {i.untracedWriteOffQuantity.toFixed(2)}</div>}
                  {i.unpostedWriteOffQuantity > 0 && <div style={{ color: 'var(--crm-danger)', fontSize: 11, fontWeight: 700, marginTop: 3 }}>Непроведене списання: {i.unpostedWriteOffQuantity.toFixed(3)} {i.weightType}</div>}
                </td>}
                {tablePreferences.visibility.stockOut && <td style={{ ...tdStyle, textAlign: 'right' }}>{i.stockOut.toFixed(2)}</td>}
                {tablePreferences.visibility.balance && <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: i.balance > 0 ? 'var(--crm-success)' : i.balance < 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>
                  {i.balance.toFixed(2)}
                </td>}
              </tr>
              {movementProductId === i.productId && (
                <tr>
                  <td id={`inventory-movements-${i.productId}`} tabIndex={-1} colSpan={visibleColumns.length} style={{ padding: 10, background: 'var(--crm-surface-alt)' }}>
                    <strong style={{ display: 'block', marginBottom: 8, color: 'var(--crm-info)' }}>Рух товару · {i.productName}</strong>
                    {movementsLoadingId === i.productId ? 'Завантаження журналу…' : (
                      movementErrors[i.productId] ? <div role="alert" style={{ color: 'var(--crm-danger)' }}>{movementErrors[i.productId]}</div>
                      : (movementsByProduct[i.productId] || []).length === 0 ? 'Документів руху та проведених ревізій немає.' : (
                        <div style={{ display: 'grid', gap: 12 }}>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <label>Період історії{' '}
                              <select aria-label="Період історії руху" value={movementPeriod} onChange={event => setMovementPeriod(event.target.value)} style={{ padding: 8, borderRadius: 6 }}>
                                <option value="current">Після останньої закритої ревізії</option>
                                {[...movementPeriods].reverse().map(period => <option key={period.id} value={period.id}>{period.label}</option>)}
                                <option value="all">Уся історія</option>
                              </select>
                            </label>
                            {movementPeriod !== 'all' && movementPeriods.length > 0 && <label>
                              <input type="checkbox" checked={includeEarlierMovements} onChange={event => setIncludeEarlierMovements(event.target.checked)} /> Включити попередні періоди
                            </label>}
                          </div>
                          <small style={{ color: 'var(--crm-text-muted)' }}>{movementPeriods.length
                            ? 'Період починається з фактичного залишку закритої ревізії. Залишки біля рухів збережено за даними журналу; фільтр не перераховує склад.'
                            : 'Закритої ревізії для цього товару немає — показано всю історію.'}</small>
                          {unpostedWriteOffPeriods.length > 0 && (
                              <section aria-label="Підсумок непроведених списань між ревізіями" style={{ border: '1px solid var(--crm-danger)', borderRadius: 6, background: 'var(--crm-danger-bg)', color: 'var(--crm-text)', padding: 10 }}>
                                <strong style={{ display: 'block', color: 'var(--crm-danger)', marginBottom: 7 }}>Непроведене списання між ревізіями</strong>
                                <div style={{ display: 'grid', gap: 5 }}>
                                  {unpostedWriteOffPeriods.map((period, index) => {
                                    const start = period.startRevision
                                      ? `${period.startRevision.label} (${new Date(period.startRevision.movementDate).toLocaleDateString('uk-UA')})`
                                      : 'До першої ревізії';
                                    const end = period.endRevision
                                      ? `${period.endRevision.label} (${new Date(period.endRevision.movementDate).toLocaleDateString('uk-UA')})`
                                      : 'Зараз';
                                    const caption = period.isCurrent
                                      ? period.startRevision ? `Після ${start}` : 'Без проведених ревізій'
                                      : `${start} → ${end}`;
                                    return (
                                      <div key={`${period.startRevision?.label ?? 'before'}-${period.endRevision?.label ?? 'current'}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
                                        <span>{caption}</span>
                                        <strong style={{ color: period.quantity > 0 ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>{period.quantity.toFixed(3)} {i.weightType}</strong>
                                      </div>
                                    );
                                  })}
                                </div>
                              </section>
                          )}
                          {chronologicalMovements.map(movement => {
                            const isDraft = movement.status.toLowerCase() === 'draft';
                            const isUnpostedWriteOff = movement.type === 'unpostedWriteOff';
                            const amount = isDraft || isUnpostedWriteOff ? movement.pendingDelta : movement.quantityDelta;
                            const movementNote = getMovementNote(movement.notes);
                            return (
                              <div key={movement.id} style={{ borderLeft: `4px solid ${movement.isStockoutTransition || isUnpostedWriteOff ? 'var(--crm-danger)' : isDraft ? 'var(--crm-warning)' : movement.isRevisionBaseline ? 'var(--crm-blocked)' : 'var(--crm-success)'}`, borderRadius: 5, padding: '8px 10px', background: isUnpostedWriteOff ? 'var(--crm-danger-bg)' : 'var(--crm-surface)', color: 'var(--crm-text)', display: 'grid', gap: 4 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                                  <strong style={{ overflowWrap: 'anywhere' }}>{movement.label}</strong>
                                  <span style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--crm-text-muted)' }}>
                                    {isDraft ? `Чернетка з ${new Date(movement.movementDate).toLocaleString('uk-UA')}` : new Date(movement.movementDate).toLocaleString('uk-UA')}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
                                  {isUnpostedWriteOff
                                    ? <span style={{ color: 'var(--crm-danger)', fontWeight: 700 }}>НЕПРОВЕДЕНЕ СПИСАННЯ · нестача {amount?.toFixed(3)} {i.weightType} · не віднімається від майбутніх приходів</span>
                                    : isDraft
                                    ? <span style={{ color: 'var(--crm-warning)', fontWeight: 700 }}>НЕ ПРОВЕДЕНО · не впливає на залишок · {amount != null ? `очікувано ${amount > 0 ? '+' : ''}${amount.toFixed(3)} ${i.weightType}` : ''}</span>
                                    : movement.isRevisionBaseline
                                      ? <span style={{ color: 'var(--crm-blocked)', fontWeight: 700 }}>ФАКТИЧНИЙ ЗАЛИШОК РЕВІЗІЇ: {movement.balanceAfter.toFixed(3)} {i.weightType}</span>
                                      : <span style={{ color: movement.quantityDelta < 0 ? 'var(--crm-danger)' : 'var(--crm-success)', fontWeight: 700 }}>{movement.quantityDelta > 0 ? '+' : ''}{movement.quantityDelta.toFixed(3)} {i.weightType} · залишок {movement.balanceAfter.toFixed(3)}</span>}
                                  {movement.isStockoutTransition && <span style={{ color: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>ЗАЛИШОК ВИЧЕРПАНО</span>}
                                  {isDraft && movement.documentDate && <span style={{ color: 'var(--crm-text-muted)' }}>Дата документа: {new Date(movement.documentDate).toLocaleDateString('uk-UA')}</span>}
                                  {!isDraft && movement.postedAtUtc && <span style={{ color: 'var(--crm-text-muted)' }}>Проведено {new Date(movement.postedAtUtc).toLocaleString('uk-UA')}{movement.postedByUsername ? ` · ${movement.postedByUsername}` : ''}</span>}
                                  {movementNote && <span style={{ color: 'var(--crm-text-muted)' }}>{movementNote}</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )
                    )}
                  </td>
                </tr>
              )}
              {expandedProductId === i.productId && (
                <tr>
                  <td id={`inventory-lots-${i.productId}`} tabIndex={-1} colSpan={visibleColumns.length} style={{ padding: 10, background: 'var(--crm-surface-alt)' }}>
                    <strong>Партії · {i.productName}</strong>
                    <p>Історія партій включає майбутні приходи. Залишки партій — за алокаціями, не за ревізією.</p>
                    {lotsLoadingId === i.productId ? 'Завантаження…' : (
                      lotErrors[i.productId] ? <div role="alert">{lotErrors[i.productId]}</div> :
                      (lotsByProduct[i.productId] || []).length === 0 ? 'Партій приходу немає.' :
                      <div style={{ display: 'grid', gap: 8 }}>
                        {(lotsByProduct[i.productId] || []).map(lot => (
                          <div key={lot.receiptItemId} style={{ border: `1px solid ${lot.qualityStatus === 'Available' ? 'var(--crm-border)' : 'var(--crm-blocked)'}`, borderRadius: 7, padding: 9, background: lot.qualityStatus === 'Available' ? 'var(--crm-surface)' : 'var(--crm-blocked-bg)', color: 'var(--crm-text)' }}>
                            <strong>{lot.lotNumber || `Партія з накладної #${lot.receiptId}`}</strong>
                            <span style={{ marginLeft: 8, borderRadius: 10, padding: '2px 7px', fontSize: 10, background: lot.qualityStatus === 'Available' ? 'var(--crm-success-bg)' : 'var(--crm-blocked-bg)', color: lot.qualityStatus === 'Available' ? 'var(--crm-success)' : 'var(--crm-blocked)' }}>
                              {qualityLabel(lot.qualityStatus)}
                            </span>
                            <span style={{ marginLeft: 8, color: 'var(--crm-text-muted)' }}>
                              {lot.supplierName || 'Без постачальника'} · {lot.invoiceNumber || 'без номера'} · {new Date(lot.receiptDate).toLocaleDateString('uk-UA')}
                            </span>
                            <div style={{ marginTop: 4 }}>
                              Отримано {lot.receivedQuantity.toFixed(2)}, використано {lot.allocatedQuantity.toFixed(2)}, залишок <strong>{lot.remainingQuantity.toFixed(2)} {lot.weightType}</strong>, ціна {lot.unitCost.toFixed(2)} грн.
                              {lot.expirationDate && <span style={{ marginLeft: 8, color: new Date(lot.expirationDate) <= new Date(Date.now() + 14 * 86400000) ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>Придатний до {new Date(lot.expirationDate).toLocaleDateString('uk-UA')}</span>}
                            </div>
                            {lot.qualityReason && <div style={{ color: 'var(--crm-blocked)', marginTop: 4 }}>Причина: {lot.qualityReason}</div>}
                            {lot.qualityEvents.length > 0 && (
                              <details style={{ marginTop: 6, fontSize: 11, color: 'var(--crm-text-muted)' }}>
                                <summary style={{ cursor: 'pointer' }}>Історія якості ({lot.qualityEvents.length})</summary>
                                {lot.qualityEvents.map(event => (
                                  <div key={event.id} style={{ marginTop: 4, marginLeft: 10 }}>
                                    {new Date(event.changedAtUtc).toLocaleString('uk-UA')} · {qualityLabel(event.fromStatus as InventoryLotTrace['qualityStatus'])} → {qualityLabel(event.toStatus as InventoryLotTrace['qualityStatus'])}
                                    {event.reason ? ` · ${event.reason}` : ''}{event.changedByUsername ? ` · ${event.changedByUsername}` : ''}
                                  </div>
                                ))}
                              </details>
                            )}
                            {canUpdateQuality && lot.receiptItemId > 0 && lot.remainingQuantity > 0 && (
                              <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                                {lot.qualityStatus !== 'Available' && <button disabled={qualityUpdatingId === lot.receiptItemId} onClick={() => updateLotQuality(i.productId, lot.receiptItemId, 'Available')} style={{ border: 0, borderRadius: 5, padding: '4px 8px', cursor: 'pointer', background: 'var(--crm-success-bg)', color: 'var(--crm-success)' }}>Повернути в обіг</button>}
                                {lot.qualityStatus !== 'Hold' && <button disabled={qualityUpdatingId === lot.receiptItemId} onClick={() => updateLotQuality(i.productId, lot.receiptItemId, 'Hold')} style={{ border: 0, borderRadius: 5, padding: '4px 8px', cursor: 'pointer', background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)' }}>Карантин</button>}
                                {lot.qualityStatus !== 'Recalled' && <button disabled={qualityUpdatingId === lot.receiptItemId} onClick={() => updateLotQuality(i.productId, lot.receiptItemId, 'Recalled')} style={{ border: 0, borderRadius: 5, padding: '4px 8px', cursor: 'pointer', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)' }}>Відкликати</button>}
                              </div>
                            )}
                            {lot.movements.length > 0 && (
                              <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginTop: 4 }}>
                                Рухи: {lot.movements.map(movement => {
                                  const source = movement.writeOffId
                                    ? `списання #${movement.writeOffId}`
                                    : movement.adjustmentId
                                      ? `операція #${movement.adjustmentId}`
                                      : movement.reason || movement.movementType;
                                  return `${source}${movement.eventOrderId ? ` → подія #${movement.eventOrderId}` : ''}: ${movement.quantity.toFixed(2)}`;
                                }).join('; ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {filtered.length === 0 && !loading && !error && (
            <tr>
              <td colSpan={visibleColumns.length} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Дані відсутні</td>
            </tr>
          )}
        </tbody>
      </table>
      {!loading && !error && displayed.length > 0 && (
        <nav className="inventory-balance-navigation" aria-label="Сторінки залишків">
          <button type="button" disabled={currentPage === 0} onClick={() => setPageIndex(currentPage - 1)}>Попередня сторінка</button>
          <span>Сторінка {currentPage + 1} з {pageCount} · товари {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, displayed.length)} з {displayed.length}</span>
          <button type="button" disabled={currentPage + 1 >= pageCount} onClick={() => setPageIndex(currentPage + 1)}>Наступна сторінка</button>
        </nav>
      )}
    </div>
  );
};

export default InventoryBalancePage;
